// src/lib/errors/ErrorService.ts

import { toast } from 'sonner';
import { toastDuration } from '@/components/ui/sonner';
import { classifyError } from './ErrorClassifier';
import { getErrorPresentation } from './ErrorMessages';
import { reportError, type ErrorContext } from './ErrorReporter';
import { isCrispSupportAvailable } from '@/hooks/useCrisp';

export interface ApiError extends Error {
  /** Code backend stable SCREAMING_SNAKE_CASE (ex: VALIDATION_ERROR) */
  errorCode?: string;
  /** Code HTTP */
  statusCode?: number;
  /** requestId pour corrélation support — extrait du header X-Request-Id */
  requestId?: string;
  /** Détails additionnels du backend */
  details?: Record<string, unknown>;
}

export interface HandleOptions {
  /** Message user-friendly custom (override le message calculé automatiquement) */
  userMessage?: string;
  /** Contexte pour PostHog */
  context?: ErrorContext;
  /** Si true, n'affiche pas de toast (log + PostHog seulement) */
  silent?: boolean;
}

/**
 * Crisp est un widget global, donc l'action du toast ne peut pas appeler
 * `useCrisp` (un hook React) directement. Le garde rend l'action sûre lorsque
 * le widget n'est pas encore chargé ou a été désactivé dans l'environnement.
 */
function openSupportChat(): void {
  // Toast actions can outlive a logout or a public-page navigation.
  if (typeof window === 'undefined' || !isCrispSupportAvailable()) return;

  const crisp = (window as Window & { $crisp?: { push: (command: unknown[]) => void } }).$crisp;
  if (crisp && typeof crisp.push === 'function') {
    try {
      crisp.push(['do', 'chat:open']);
    } catch {
      // Le CTA de support ne doit jamais transformer une erreur en crash UI.
    }
  }
}

/**
 * Point d'entrée unique pour toutes les erreurs de l'application.
 * Pipeline : classifie → log dev → PostHog → toast Sonner neutre.
 *
 * @example
 * try {
 *   await apiClient.post('/documents', data);
 * } catch (err) {
 *   ErrorService.handle(err, { context: { action: 'upload_document', case_id } });
 * }
 */
export class ErrorService {
  static handle(error: unknown, options: HandleOptions = {}): void {
    // Extraire les champs ApiError avec des guards — error peut être n'importe quoi
    const isApiError = error instanceof Error && 'statusCode' in error;
    const apiStatusCode = isApiError ? (error as ApiError).statusCode : undefined;
    const apiErrorCode = isApiError ? (error as ApiError).errorCode : undefined;
    const apiRequestId = isApiError ? (error as ApiError).requestId : undefined;
    const backendMessage = error instanceof Error ? error.message : undefined;

    const { category, reason, statusCode, technicalMessage, retryable } = classifyError(
      error,
      apiStatusCode
    );

    // 1. Log structuré — actif dans tous les environnements pour diagnostiquer les erreurs prod
    console.error(
      `[ErrorService] ${category.toUpperCase()} | ` +
      `reason=${reason} | ` +
      `retryable=${retryable} | ` +
      `status=${statusCode ?? 'n/a'} | ` +
      `code=${apiErrorCode ?? 'n/a'} | ` +
      `requestId=${apiRequestId ?? 'n/a'} | ` +
      `url=${options.context?.url ?? window.location.pathname}`,
      error
    );

    // 2. Reporter vers PostHog Error Tracking (captureException)
    // Les transports transitoires marqués silent sont gérés gracieusement par
    // l'appelant (ex: requêtes de fond qui retournent []) ; les 5xx restent
    // reportés car ils constituent un signal Hector actionnable.
    const isHandledTransportNoise =
      options.silent &&
      (reason === 'transport_unreachable' || reason === 'transport_timeout');
    if (!isHandledTransportNoise) {
      reportError({
        category,
        reason,
        retryable,
        statusCode,
        technicalMessage,
        errorCode: apiErrorCode,
        requestId: apiRequestId,
        context: options.context,
        originalError: error,
      });
    }

    // 3. Toast utilisateur (style neutre, jamais de rouge)
    if (!options.silent) {
      const presentation = getErrorPresentation(
        category,
        backendMessage,
        statusCode,
        reason
      );
      const message = options.userMessage ?? presentation.message;

      // Les 401 redirigent immédiatement via apiClient → toast inutile
      if (category === 'auth' && statusCode === 401) return;

      const hasRequestId = !!apiRequestId &&
        (category === 'network' || category === 'unexpected');

      toast(message, {
        // Plus de temps si requestId affiché (pour que l'user puisse le noter/copier)
        ...toastDuration(hasRequestId ? 15000 : 10000),
        description: hasRequestId
          ? `Référence support : ${apiRequestId}`
          : undefined,
        action:
          presentation.action === 'contact_support' && isCrispSupportAvailable()
            ? { label: 'Contacter le support', onClick: openSupportChat }
            : undefined,
      });
    }
  }

  /**
   * Variante pour les Error Boundaries React (erreurs JS inattendues).
   * Ne pas afficher de toast ici — le boundary affiche déjà un fallback UI.
   *
   * @param componentStack - Fourni par React via ErrorInfo.componentStack — utile pour cibler le composant crashé
   */
  static handleBoundaryError(error: Error, context?: ErrorContext, componentStack?: string): void {
    reportError({
      category: 'unexpected',
      technicalMessage: error.message,
      context: {
        ...context,
        url: window.location.pathname,
      },
      originalError: error,
    });

    // Always log boundary errors — these are critical crashes hidden behind fallback UI
    console.error(
      `[AppErrorBoundary] ${context?.action ?? 'unknown'} | ${error.message}`,
      { error, componentStack, context }
    );
  }
}
